import {caseInsensitiveContains, caseInsensitiveCompare} from '../../../globals/helpers'
import constants from '../../../globals/constants.js'
import _ from "underscore";

const genericManufacturerId = 'a46d3817-a8a8-47e4-8dbf-8e3704702642'

export default {
    siteName: (state) => {
        return state.site?.name
    },

    siteElectricityPrice: (state) => {
        return state.site?.electricity_price
    },

    siteFeedInTariff: (state) => {
        return state.site?.feed_in_tariff
    },

    siteCurrency: (state) => {
        return state.site?.currency
    },

    isGatewayOnline: (_state, getters) => {
        return getters.gatewayState === 'online'
    },

    gatewayState: (state) => {
        return state.site && state.site.status ? state.site.status.gateways_online_state : null
    },

    hasErrors: (state) => {
        return state.errors.length !== 0
    },

    hasMessages: (state) => {
        return state.messages.length !== 0
    },

    supportedThings: (state) => {
        if (state.deviceTemplateThings) {
            return _.flatten(_.values(state.deviceTemplateThings))
        } else {
            return []
        }
    },

    supportedEmsCapabilities: (state) => {
        return state.emsCapabilities
    },

    supportedSlaveSpecifications: (state) => {
        return state.slaveSpecifications
    },

    availableExtensionsForThingType: (state, getters) => (type, extensionId) => {
        return state.plannedExtensions.filter(extension => {
            const hasEmsCapability = hasEmsCapabilityForType(state, extension.productReferenceUuid, type)
            const hasUnusedExtension = unusedExtension(getters.plannedProductThings, extension.extensionUuid, extensionId)
            return !extension._removed && hasEmsCapability && hasUnusedExtension
        })
    },

    availableExtensionsForCapabilityType: (state, getters) => (type) => {
        const matchingExtensionProductIds = Object.entries(state.emsCapabilities)
            .filter(([_productId, capabilities]) => capabilities.some(capability => capability?.type === type))
            .map(([productId, _capabilities]) => productId)
        return state.plannedExtensions.filter(extension => {
            return !extension._removed &&
                matchingExtensionProductIds.includes(extension.productReferenceUuid)
        })
    },

    availableExternalPlantControlTypes: (state) => {
        return state.externalPlantControls.map(epc => epc.type)
    },

    availableExtensionsForExternalPlantControlType: (state) => (type) => {
        // Find all extension IDs that support the given external plant control type
        const matchingExtensionProductIds = Object.entries(state.supportedExternalPlantControlTypes)
            .filter(([_productId, epc_types]) => epc_types.includes(type))
            .map(([productId, _epc_types]) => productId)

        // Return all planned extensions that match the found IDs
        return state.plannedExtensions.filter(extension => {
            return !extension._removed &&
                matchingExtensionProductIds.includes(extension.productReferenceUuid)
        })
    },

    thingsWithParagraph14aControl: (state) => {
        const filteredThings = []

        state.plannedProducts.forEach(product => {
            if (product._removed) return

            (product.devices || []).forEach(device => {
                (device.things || []).forEach(thing => {
                    const typeSpecificInfo = thing.typeSpecificInformation || thing.productTypeSpecificInformation || {}
                    const metaData = [
                        ...Object.values(typeSpecificInfo).filter(info => info != null && info !== undefined),
                        thing.thingMetadata || {}
                    ];

                    const hasParagraph14aControl = metaData.some(thingInfo => thingInfo?.paragraph14aControlAllowed === true)

                    if (hasParagraph14aControl) {
                        filteredThings.push({
                            ...thing,
                            product: product,
                            device: device
                        })
                    }
                })
            })
        })

        return filteredThings
    },

    thingsWithGridLimitationFeedInControl: (state) => (sourceSignal) => {
        const filteredThings = []

        state.plannedProducts.forEach(product => {
            if (product._removed) return

            (product.devices || []).forEach(device => {
                (device.things || []).forEach(thing => {
                    const typeSpecificInfo = thing.typeSpecificInformation || thing.productTypeSpecificInformation || {}
                    const metaData = [
                        ...Object.values(typeSpecificInfo).filter(info => info != null && info !== undefined),
                        thing.thingMetadata || {}
                    ];

                    const hasGridLimitationControl = metaData.some(thingInfo => {
                        if (!thingInfo) return false
                        const feedInLimitationControl = thingInfo.feedInLimitationControl
                        if (!Array.isArray(feedInLimitationControl)) {
                            return false
                        }

                        if (sourceSignal) {
                            return feedInLimitationControl.includes(sourceSignal)
                        } else {
                            return feedInLimitationControl.includes(constants.GRID_LIMITATION_SOURCE_SIGNALS.RIPPLE_CONTROL) ||
                                   feedInLimitationControl.includes(constants.GRID_LIMITATION_SOURCE_SIGNALS.PARAGRAPH_9)
                        }
                    })

                    if (hasGridLimitationControl) {
                        filteredThings.push({
                            ...thing,
                            product: product,
                            device: device
                        })
                    }
                })
            })
        })

        return filteredThings
    },

    availableThingsForExternalPlantControlType: (state) => (type) => {
        return state.plannedProducts.flatMap(product => {
            const masterDevices = (product.devices || []).filter(device => !device.masterId)
            const things = masterDevices.flatMap(device => device.things || []).filter(thing => {
                return !thing.extensionId &&
                    caseInsensitiveContains(thing.thingMetadata?.supportedExternalPlantControlTypes, type)
            }).filter(thing => !thing._removed)
            return things.map(thing => ({
                ...thing,
                productName: product.name
            }))
        })
    },

    slaveOnlyDevices: (state) => {
        return state.slaveOnlyDevices
    },

    gridMeters: (_state, getters) => {
        const meters = getters.thingsByThingType(constants.METER_TYPES)

        const filterCondition = meter => {
            return meter.thingMetadata['meterUsageType'] === 'grid'
        }

        return meters.filter(filterCondition)
    },

    hasGridMeter: (_state, getters) => {
        return getters.gridMeters.length > 0
    },

    hasConsumer: (_state, getters) => {
        return getters.thingsByThingType(constants.THING_TYPE.CONSUMER).length > 0
    },

    plannedProductThings: (state) => {
        return state.plannedProducts.flatMap(product => {
            return product.devices.flatMap(device => {
                return device.things
            })
        })
    },

    plannedProductDevices: (state) => {
        return state.plannedProducts.flatMap(product => {
            return product.devices
        })
    },

    thingsByThingType: (state, getters) => (types) => {
        let compareFct;
        if (Array.isArray(types)) {
            compareFct = caseInsensitiveContains
        } else {
            compareFct = caseInsensitiveCompare
        }

        return state.plannedThings.concat(getters.plannedProductThings).filter(t => compareFct(types, t.type) && !t._removed)
    },

    specificationPresent: (state) => (id) => {
        return state.protocolDefaults[id] !== undefined
    },

    findDatapoint: (state) => (configId, thingType, key) => {
        const datapoints = state.deviceTemplateDatapoints[configId] || []
        return datapoints.find(dp => dp.things?.[thingType]?.key === key)
    },

    possibleValuesForDatapoint: (_state, getters) => (configId, thingType, key) => {
        return getters.findDatapoint(configId, thingType, key)?.possibleValues?.enum || []
    },

    productForExtension: (state, getters) => (extension) => {
        for (const productModel of getters.supportedProductModels) {
            if (!productModel.is_extension) {
                for (const productModelVersion of productModel.product_model_versions) {
                    for (const deviceTemplate of productModelVersion.device_templates) {
                        for (const deviceSpecification of deviceTemplate.device_specifications) {
                            if (deviceSpecification.config_id == extension.productReferenceUuid) {
                                return {
                                    productModel: state.productModels[productModel.id],
                                    productModelVersion: state.productModelVersions[productModelVersion.id],
                                    deviceTemplate: deviceTemplate,
                                    deviceSpecification: deviceSpecification
                                }
                            }
                        }
                    }
                }
            }
        }

        return null
    },

    supportedProductModels: function (state) {
        return _.filter(Object.values(state.productModels), (productModel) => {
            return state.supportedProductIds.includes(productModel.id)
        })
    },

    extensionManufacturers: function (state, getters) {
        const manufacturerIds = getters.extensionProductModels.map(model => model.manufacturer_id)
        return _.filter(Object.values(getters.supportedManufacturers), (manufacturer) => {
            return manufacturerIds.includes(manufacturer.id)
        })
    },

    extensionModels: function (state, getters) {
        return state.extensionModels
    },

    extensionProductModels: function (_state, getters) {
        return _.filter(Object.values(getters.supportedProductModels), (productModel) => {
            return productModel.is_extension
        })
    },

    extensionInUse: (state) => (extension) => {
        return state.plannedProducts.some(product => {
            return product.extension?.extensionUuid === extension.extensionUuid
        })
    },

    extensionProtocols: (state) => (extensionId) => {
      return state.extensionProtocols[extensionId] || null
    },

    supportedProductModelVersions: function (state) {
        if (!state.productModelVersions || !state.supportedProductIds) {
            return []
        }

        let supportedProductModelVersions = []
        const keys = Object.keys(state.productModelVersions)
        keys.forEach(key => {
            const productModelVersion = state.productModelVersions[key]

            const availableStatus = ['published', 'test'].includes(productModelVersion.status)
            const publishedOrEditingProduct = availableStatus ||
                state.editingProduct?.productModelVersionUuid === productModelVersion.id

            if (publishedOrEditingProduct && (state.supportedProductIds.includes(key))) {
                supportedProductModelVersions.push(productModelVersion)
            }
        })

        return supportedProductModelVersions
    },

    supportedManufacturers: function(state, getter) {
        const indexById = (acc, item) => {
          acc[item.id] = item
          return acc
        }
        const productManufacturers = getter.supportedProductModelVersions.map(product => product.manufacturer)
        const manufacturersHash = _.reduce(productManufacturers, indexById, {})

        return _.sortBy(Object.values(manufacturersHash), function(manufacturer) {
            return [manufacturer.name !== 'neoom', !manufacturer.is_neoom, manufacturer.name.toLowerCase()]
        })
    },

    groupedSupportedManufacturers: function(state, getter) {
        let groupedManufacturer = {
            'neoom': {},
            'vue.site_config_tool.manufacturer_generic': {},
            'vue.site_config_tool.manufacturer_no_partner': []
        }
        for (const manufacturer of getter.supportedManufacturers) {
            if (manufacturer.name == 'neoom') {
                groupedManufacturer['neoom'] = manufacturer
            } else if(manufacturer.id == genericManufacturerId) {
                groupedManufacturer['vue.site_config_tool.manufacturer_generic'] = manufacturer
            } else {
                groupedManufacturer['vue.site_config_tool.manufacturer_no_partner'].push(manufacturer)
            }
        }
        return groupedManufacturer
    },

    groupedManufacturersFilteredByIds: (state, getter) => (manufacturerIds) => {
        if (!manufacturerIds?.length) return getter.groupedSupportedManufacturers

        const filtered = {}
        for (const [key, value] of Object.entries(getter.groupedSupportedManufacturers)) {
            if (Array.isArray(value)) {
                const filteredMfgs = value.filter(m => manufacturerIds.includes(m.id))
                if (filteredMfgs.length) filtered[key] = filteredMfgs
            } else if (manufacturerIds.includes(value.id)) {
                filtered[key] = value
            }
        }
        return filtered
    },

    thingsDirty: function (state) {
        for (let p of state.plannedProducts) {
            if (p._dirty) {
                return true
            }
        }
        for (let t of state.plannedThings) {
            if (t._dirty) {
                return true
            }
        }

        return false
    },

    extensionsDirty: function (state) {
        for (let extension of state.plannedExtensions) {
            if (extension._dirty) {
                return true
            }
        }
    },

    thingsMarkedForRemovalExists: function (state) {
        const finder = (e) => e._removed

        state.plannedThings.some(finder)
    },

    softwareDirty: function (state, getters) {
        return !!state.emsData._dirty || getters.externalPlantControlsDirty
    },

    externalPlantControlsDirty: function (state) {
        for (let epc of state.externalPlantControls) {
            if (epc._dirty) {
                return true
            }
        }
        return false
    },

    isSimulationActive: function (state) {
        return !!state.generatedConfig?.simulated
    },

    savedConfigThings: (state) => {
        return state.savedConfig.products.flatMap(product => {
            return product.devices.flatMap(device => {
                return device.things
            })
        }).concat(state.savedConfig.things)
    },

    residualThings: (state) => {
        return state.residualThings ?? []
    },

    isThingPersisted: (_state, getters) => (id) => {
        const savedThingIds = getters.savedConfigThings.map(thing => thing.thingUuid)
        return savedThingIds.includes(id)
    },

    getProductExtendedName: (state) => (product) => {
        const productModelVersion = state.productModelVersions[product.productModelVersionUuid]
        if (!productModelVersion) return ''

        const manufacturerName = productModelVersion.manufacturer?.name || ''
        const productModelName = productModelVersion.productModel?.name || ''
        const productModelVersionName = productModelVersion.name || ''

        return [manufacturerName, productModelName, productModelVersionName]
            .filter(name => name)
            .join(' - ')
    },
}

function hasEmsCapabilityForType(state, id, type) {
    return state.emsCapabilities[id]?.some(capability => {
        return caseInsensitiveCompare(capability.type, type) ||
               caseInsensitiveCompare(capability.associatedThingType, type)
    }) ?? false
}

function unusedExtension(things, id, extensionId) {
    const notAvailableExtensionIds = things
        .map(thing => thing.extensionId)
        .filter(id => id !== undefined && id != extensionId)
    return !notAvailableExtensionIds.includes(id)
}
